import React, { useState } from "react";
import { Modal, Button, Upload, Form, Input, Space, Card, message } from "antd";
import { InboxOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd";
import { uploadDocumentWithMetadata } from "../../api/api";

type MetadataField = {
  key: string;
  value: string;
};

type FileWithMetadata = {
  file: File;
  metadata: MetadataField[];
};

type IngestDocumentModalProps = {
  show: boolean;
  handleClose: () => void;
};

function IngestDocumentModal({ show, handleClose }: IngestDocumentModalProps) {
  const initialMetadataFields: MetadataField[] = [
    { key: "title", value: "" }
  ];

  const [files, setFiles] = useState<FileWithMetadata[]>([]);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setFiles([]);
    setError(null);
  };

  const handleFileChange = (info: any) => {
    const newFiles = info.fileList.map((fileObj: any) => ({
      file: fileObj.originFileObj,
      metadata: [...initialMetadataFields],
    }));
    setFiles(newFiles);
  };

  const handleAddField = (fileIndex: number) => {
    const updatedFiles = [...files];
    updatedFiles[fileIndex].metadata.push({ key: "", value: "" });
    setFiles(updatedFiles);
  };

  const handleMetadataChange = (
    fileIndex: number,
    metadataIndex: number,
    field: 'key' | 'value',
    value: string
  ) => {
    const updatedFiles = [...files];
    updatedFiles[fileIndex].metadata[metadataIndex][field] = value;
    setFiles(updatedFiles);
  };

  const handleSubmit = async () => {
    for (const fileObj of files) {
      const titleField = fileObj.metadata.find(
        (field) => field.key.toLowerCase() === "title"
      );

      if (!titleField || titleField.value.trim() === "") {
        setError(`title is required for file: ${fileObj.file.name}`);
        return;
      }
    }

    setError(null);

    try {
      const uploadPromises = files.map(async (fileObj) => {
        const formData = new FormData();
        formData.append("file", fileObj.file);
        formData.append("metadata", JSON.stringify(fileObj.metadata));

        return uploadDocumentWithMetadata(formData);
      });

      await Promise.all(uploadPromises);
      message.success("Documents uploaded successfully");
      resetForm();
      handleClose();
    } catch (error) {
      console.error("Error uploading documents:", error);
      message.error("Error uploading documents");
    }
  };

  const handleModalClose = () => {
    resetForm();
    handleClose();
  };

  return (
    <Modal
      title="Ingest Documents"
      open={show}
      onCancel={handleModalClose}
      footer={[
        <Button key="cancel" onClick={handleModalClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={800}
      centered
    >
      <Upload.Dragger
        multiple
        beforeUpload={() => false}
        onChange={handleFileChange}
        style={{ marginBottom: 16 }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Attach documents to be uploaded for Ingestion</p>
        <p className="ant-upload-hint">Click or drag files to this area to upload</p>
      </Upload.Dragger>

      {files.map((fileObj, fileIndex) => (
        <Card
          key={fileIndex}
          title={`File: ${fileObj.file.name}`}
          size="small"
          style={{ marginBottom: 16 }}
        >
          {fileObj.metadata.map((field, metadataIndex) => (
            <Space key={metadataIndex} style={{ width: '100%', marginBottom: 8 }} align="baseline">
              <Input
                placeholder="Key"
                value={field.key}
                onChange={(e) =>
                  handleMetadataChange(fileIndex, metadataIndex, 'key', e.target.value)
                }
                readOnly={field.key.toLowerCase() === "title"}
                style={{ width: 150 }}
              />
              <Input
                placeholder="Value"
                value={field.value}
                onChange={(e) =>
                  handleMetadataChange(fileIndex, metadataIndex, 'value', e.target.value)
                }
                required={field.key.toLowerCase() === "title"}
                style={{ flex: 1 }}
              />
            </Space>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => handleAddField(fileIndex)}
            style={{ width: '100%', marginTop: 8 }}
          >
            Add Metadata Field
          </Button>
        </Card>
      ))}

      {error && <div style={{ color: 'red', marginTop: 12 }}>{error}</div>}
    </Modal>
  );
}

export default IngestDocumentModal;
